import React, { useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { Query } from '../../_common';
import JobLoadsManagementHeader from '../../Header/JobLoadsManagementHeader';
import { GET_LOADS_BY_JOB_ID } from '../../../graphql/client/queries';
import { GetLoadList, GetLoadList_getLoadListByJobId as Load } from '../../../types/schemaTypes';
import LoadList from '../../TableList/LoadList';
import './LoadManagement.scss';
import { Error } from '../../_common';
import { JobLoadsFilters } from '../../Filter';
import { useStoreContext } from '../../App/StoreProvider';
import { DELETE_LOADS } from '../../../graphql/client/mutations';
import DeleteModal from '../DeleteModal';
import { LoadStatusEnum } from '../../../types/LoadStatusEnum';
import { useParams } from 'react-router-dom';

interface SelectedLoad {
    loadId: number | null;
    boxNumber: string | null;
}

// Do not change this value unless the associate backend value has been updated - (8/22/19) BS
const PAGE_SIZE = 50;

const LoadManagement = ({ match }: any): React.ReactElement => {
    const initialArray: SelectedLoad[] = [];
    const loadIds: any = [];
    const [selectedLoads, setSelectedLoads] = useState(initialArray);
    const [state] = useStoreContext();
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isSearchOnLoadId, setIsSearchOnLoadId] = useState(true);
    const [loadsJobName, setLoadsJobName] = useState('');
    const [tabTitle, setTabTitle] = useState(document.title);
    const abortController = React.useRef(new AbortController());
    const [listHeight, setListHeight] = useState(window.screen.height);

    const [showFilter, setShowFilter] = useState(false);
    const [dateRangeFilter, setDateRangeFilter] = useState<string | null>('1week');

    const {
        loadList: { filters, searchPhrase },
    } = state;
    const {jobId} = useParams()
    const jobIdVal:any = jobId
    
    const variables = React.useMemo(()=>{
        let finalDateFilter = '1week';
        if (dateRangeFilter !== null && dateRangeFilter !== undefined && dateRangeFilter !== '') {
            finalDateFilter = dateRangeFilter;
        }
        return {
        input: {
            jobId: parseInt(jobIdVal),
            carriers: filters.carrierIds,
            drivers: filters.driverIds,
            lofas: filters.lofaIds,
            proppants: filters.proppantIds,
            statuses: filters.statuses,
            isSearchOnLoadId: searchPhrase ? isSearchOnLoadId : true,
            search: searchPhrase,
            dateRangeFilter: finalDateFilter,
        },
    }},[jobIdVal,filters.carrierIds,filters.driverIds,filters.lofaIds,filters.proppantIds,filters.statuses,searchPhrase,isSearchOnLoadId,dateRangeFilter ]);
    const onCheckAll = (checkedStatus: boolean, loadsList: Load[]): void => {
        const result: SelectedLoad[] = [];
        if (checkedStatus === true) {
            loadsList.forEach((el: Load): void => {
                if (el.status === LoadStatusEnum.CREATED || el.status === LoadStatusEnum.CREATED_NOT_SAVED) {
                    result.push({ loadId: el.loadId, boxNumber: el.boxNumber });
                }
            });
        }
        setSelectedLoads(result);
    };

    // TODO: Extract Query to useQuery to stop Filters section from reloading everytime
    selectedLoads.forEach(it => {
        loadIds.push(it.loadId);
    });
    const [deleteLoad] = useMutation(DELETE_LOADS, {
        onCompleted() {
            setSelectedLoads(initialArray);
            setShowDeleteModal(false);
        },
        refetchQueries: () => {
            return [
                {
                    query: GET_LOADS_BY_JOB_ID,
                    variables: {
                        input: {
                            jobId: parseInt(jobIdVal),
                            carriers: filters.carrierIds,
                            drivers: filters.driverIds,
                            lofas: filters.lofaIds,
                            proppants: filters.proppantIds,
                            statuses: filters.statuses,
                            isSearchOnLoadId: isSearchOnLoadId,
                            search: searchPhrase,
                            dateRangeFilter: (dateRangeFilter !== null && dateRangeFilter !== undefined && dateRangeFilter !== '') ? dateRangeFilter : '1month',
                        },
                    },
                },
            ];
        },
    });
    const deleteLoadIds = (): void => {
        const variables = { input: { loadIds } };
        deleteLoad({ variables });
    };

    const handleCancel = (): void => {
        setShowDeleteModal(false);
        setSelectedLoads(initialArray);
    };

    const context = React.useMemo(()=>{
        return{
        fetchOptions: {
            signal: abortController.current.signal,
        },
    }},[abortController.current.signal]);

    // const context = {
    //         fetchOptions: {
    //             signal: abortController.current.signal,
    //         },
    //     }

    const abortRequest = React.useCallback((): void => {
        try{
        if(!abortController.current.signal.aborted){
             abortController.current.abort();
        }
    }catch(error){
        console.log("check", error)
    }
        
    },[abortController.current]);

    const setListContainerHeight = (): void => {
        const filterDiv = document.getElementById('loadsFilterContainer');  
        const listDiv = document.getElementById('loadsListContainer');

        let filterHeight = 0;
        // setTimeout in place to avoid animation miscalculations
        setTimeout(() => {
            if (filterDiv) {
                filterHeight = filterDiv.scrollHeight;
            }
            if (listDiv) {
                setListHeight(Math.min(filterHeight));
            }
        }, 100);
    };

    return (
        <Query<GetLoadList>
            query={GET_LOADS_BY_JOB_ID}
            variables={variables}
            pollInterval={60000}
            context={context}
            fetchPolicy="network-only"
            // nextFetchPolicy= "cache-first"
        >
            {({
                error,
                data,
                loading,
                fetchMore,
                refetch,
            }): React.ReactElement | null => {

                React.useEffect(()=>{
                    if (loadsList.length > 0) {
                        setLoadsJobName(loadsList[0].jobName);
                        document.title = loadsList[0].jobName ? loadsList[0].jobName : tabTitle;
                        setTabTitle(document.title);
                    }
                },[data]);
                if (error) {
                    return <Error error={error} />;
                }

                const loadsList: Load[] = [];

                if (data?.getLoadListByJobId && data.getLoadListByJobId.length !== 0) {
                    (data.getLoadListByJobId as (Load | null)[]).forEach((element: Load | null): void => {
                        if (element != null) {
                            loadsList.push(element);
                        }
                    });
                }
                
                const offset = data && data.getLoadListByJobId ? data.getLoadListByJobId.length / PAGE_SIZE : 0;

                let isEndOfResults = false;
                if (
                    offset % 1 !== 0 ||
                    (!isEndOfResults && data && data.getLoadListByJobId && data.getLoadListByJobId.length < PAGE_SIZE)
                ) {
                    // If offset is not a whole number, the end of the results has been reached. (8/22/19) - BS
                    isEndOfResults = true;
                }

                return (
                    <div className="LoadManagement-Container">
                        <JobLoadsManagementHeader
                            jobId={jobIdVal}
                            jobName={loadsJobName}
                            onCheckAll={checked => onCheckAll(checked, loadsList)}
                            selectedLoads={selectedLoads}
                            deleteSelectedLoads={() => setShowDeleteModal(true)}
                            isSearchOnLoadId={isSearchOnLoadId}
                            setIsSearchOnLoadId={setIsSearchOnLoadId}
                            showFilter={showFilter}
                            setShowFilter={setShowFilter}
                            dateRangeFilter={dateRangeFilter}
                            setDateRangeFilter={setDateRangeFilter}
                        />
                        <DeleteModal
                            show={showDeleteModal}
                            onCancel={handleCancel}
                            onConfirm={deleteLoadIds}
                            name="load"
                        />
                        <div className="JobLoads-Container">
                            <div className="JobLoads-BodyContainer">
                                <JobLoadsFilters
                                    abortRequest={abortRequest}
                                    jobId={parseInt(jobIdVal)}
                                    onFilterToggle={setListContainerHeight}
                                    showFilter={showFilter}
                                    setShowFilter={setShowFilter}
                                    dateRangeFilter={dateRangeFilter}
                                />
                            </div>
                        </div>
                        <div className="LoadManagement-Body">
                            <div id="loadsListContainer" className="LoadManagement-List" style={{ height: listHeight }}>
                                <LoadList
                                    jobId={parseInt(jobIdVal)}
                                    abortRequest={abortRequest}
                                    loadList={loadsList}
                                    isLoading={loading}
                                    isEndOfResults={isEndOfResults}
                                    entries={(data && data.getLoadListByJobId) || []}
                                    selectedLoads={selectedLoads}
                                    setSelectedLoads={setSelectedLoads}
                                    refetch={refetch}
                                    onLoadMore={() =>
                                        fetchMore({
                                            variables: {
                                                offset,
                                            },
                                            updateQuery: (prev, { fetchMoreResult }) => {
                                                if (
                                                    prev &&
                                                    fetchMoreResult &&
                                                    prev.getLoadListByJobId &&
                                                    fetchMoreResult.getLoadListByJobId
                                                ) {
                                                    return Object.assign({}, prev, {
                                                        getLoadListByJobId: [
                                                            ...prev.getLoadListByJobId,
                                                            ...fetchMoreResult.getLoadListByJobId,
                                                        ],
                                                    });
                                                } else {
                                                    return prev;
                                                }
                                            },
                                        })
                                    }
                                />
                            </div>
                        </div>
                    </div>
                );
            }}
        </Query>
    );
};

export default LoadManagement;
